import React, { useCallback, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Package, X } from 'lucide-react';

import { loadStorefrontView } from './api';
import { Empty } from './parts';
import { useLiveResource } from './useLive';
import { KpiRow, NUMBER, Panel, Share } from './MobileParts';
import {
  Funnel, MoneyText, ReadState, StoreTable, StorefrontGate, StorefrontHead, addMoney, count, primaryMoney, rate, ratio, st, stCount,
  useStorefrontSite,
} from './StorefrontParts';

// Every product from being shown to being bought: how often it was shown in a
// list, opened, put in a bag, taken to checkout and paid for, with its revenue
// from the orders themselves and its variants. Products are known by their
// stable code; the name comes from the catalogue.

function ProductArt({ product }) {
  return (
    <span className="xo-mob-rank-icon xo-sf-product-art">
      {typeof product.imageUrl === 'string' ? <img src={product.imageUrl} alt="" loading="lazy" decoding="async" /> : <Package aria-hidden="true" />}
    </span>
  );
}

function ProductDetail({ product, onClose }) {
  const most = Math.max(1, ...product.variants.map((variant) => variant.views));
  return (
    <Panel title={product.name || product.productCode} detail={st('products.detail_detail', { code: product.productCode })} className="xo-sf-product-detail"
      aside={<button type="button" className="xo-mob-icon-button" onClick={onClose} aria-label={st('close')}><X /></button>}>
      <Funnel steps={[
        { key: 'impressions', label: st('products.step_shown'), count: product.impressions },
        { key: 'views', label: st('products.step_viewed'), count: product.views },
        { key: 'bag', label: st('products.step_bag'), count: product.bagAdds, note: product.bagRemoves > 0 ? st('products.removed', { count: NUMBER.format(product.bagRemoves) }) : null },
        { key: 'checkout', label: st('products.step_checkout'), count: product.checkoutSessions },
        { key: 'orders', label: st('products.step_ordered'), count: product.orders, note: stCount('products.units', product.purchases) },
      ]} />
      <div className="xo-mob-row is-halves is-inner">
        <div>
          <h3 className="xo-mob-subhead">{st('products.variants_title')}</h3>
          {product.variants.length === 0 ? <p className="xo-sf-muted-line">{st('products.no_variants')}</p> : (
            <ol className="xo-mob-places xo-sf-compact-list">
              {product.variants.map((variant) => (
                <li key={variant.variant}>
                  <Package aria-hidden="true" className="xo-sf-list-icon" />
                  <span><strong>{variant.variant}</strong><small>{st('products.variant_line', { selects: NUMBER.format(variant.selects), bags: NUMBER.format(variant.bagAdds) })}</small></span>
                  <Share value={variant.views} total={most} />
                  <em>{st('products.views_count', { count: NUMBER.format(variant.views) })}</em>
                </li>
              ))}
            </ol>
          )}
        </div>
        <div>
          <h3 className="xo-mob-subhead">{st('products.revenue_title')}</h3>
          <dl className="xo-sf-mini-facts">
            <div><dt>{st('products.col_revenue')}</dt><dd><MoneyText list={product.revenue} /></dd></div>
            <div><dt>{st('products.col_orders')}</dt><dd>{count(product.orders)}</dd></div>
            <div><dt>{st('products.col_units')}</dt><dd>{count(product.purchases)}</dd></div>
            <div><dt>{st('products.col_purchase_rate')}</dt><dd>{ratio(product.purchaseRate)}</dd></div>
          </dl>
          <p className="xo-mob-footnote">{st('products.revenue_note')}</p>
        </div>
      </div>
    </Panel>
  );
}

function ProductsView({ site, store }) {
  const [days, setDays] = useState(30);
  const [searchParams, setSearchParams] = useSearchParams();
  const picked = searchParams.get('product');
  const load = useCallback(() => loadStorefrontView('products', { site: site.code, days }), [site.code, days]);
  const report = useLiveResource(['store', 'store_traffic'], load, { reconcileMs: 120000 });
  const products = useMemo(() => (report.data ? report.data.products : []), [report.data]);
  const pick = useCallback((code) => {
    const next = new URLSearchParams(searchParams);
    if (code) next.set('product', code); else next.delete('product');
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams]);
  const head = <StorefrontHead title={st('products.title')} subtitle={st('products.subtitle')} days={days} onDays={setDays} store={store} />;
  if (report.data === null) return <div className="xo-platform-page xo-mobile-page xo-sf-page">{head}<ReadState resource={report} label={st('products.loading')} /></div>;
  const views = products.reduce((sum, product) => sum + product.views, 0);
  const bagAdds = products.reduce((sum, product) => sum + product.bagAdds, 0);
  const units = products.reduce((sum, product) => sum + product.purchases, 0);
  const revenue = addMoney(products.map((product) => product.revenue));
  const selected = products.find((product) => product.productCode === picked) || null;
  const mostViews = Math.max(1, ...products.map((product) => product.views));
  const mostLists = Math.max(1, ...report.data.lists.map((list) => list.impressions));
  const columns = [
    {
      key: 'product', label: st('products.col_product'), primary: true, sortValue: (row) => (row.name || row.productCode).toLowerCase(),
      render: (row) => (
        <span className="xo-sf-product-cell">
          <ProductArt product={row} />
          <span title={row.productCode}><strong>{row.name || row.productCode}</strong></span>
        </span>
      ),
    },
    { key: 'impressions', label: st('products.col_shown'), align: 'end', wide: true, sortValue: (row) => row.impressions, render: (row) => count(row.impressions) },
    {
      key: 'views', label: st('products.col_views'), align: 'end', sortValue: (row) => row.views,
      render: (row) => <span className="xo-sf-stack-cell"><span className="xo-sf-share-cell"><Share value={row.views} total={mostViews} /><b>{count(row.views)}</b></span><small>{st('products.of_shown', { rate: ratio(row.viewRate) })}</small></span>,
    },
    {
      key: 'bag', label: st('products.col_bag'), align: 'end', sortValue: (row) => row.bagAdds,
      render: (row) => <span className="xo-sf-stack-cell"><b>{count(row.bagAdds)}</b><small>{st('products.of_views', { rate: ratio(row.bagRate) })}</small></span>,
    },
    { key: 'checkout', label: st('products.col_checkout'), align: 'end', wide: true, sortValue: (row) => row.checkoutSessions, render: (row) => count(row.checkoutSessions) },
    {
      key: 'orders', label: st('products.col_orders'), align: 'end', sortValue: (row) => row.orders,
      render: (row) => <span className="xo-sf-stack-cell"><b>{count(row.orders)}</b><small>{st('products.of_views', { rate: ratio(row.purchaseRate) })}</small></span>,
    },
    { key: 'units', label: st('products.col_units'), align: 'end', wide: true, sortValue: (row) => row.purchases, render: (row) => count(row.purchases) },
    {
      key: 'revenue', label: st('products.col_revenue'), align: 'end',
      sortValue: (row) => (primaryMoney(row.revenue) ? primaryMoney(row.revenue).netMinor : 0), render: (row) => <MoneyText list={row.revenue} />,
    },
  ];
  return (
    <div className="xo-platform-page xo-mobile-page xo-sf-page">
      {head}
      {report.error && <p className="xo-alert t-warn" role="alert">{report.error}</p>}
      <KpiRow items={[
        { label: st('products.kpi_views'), value: count(views), detail: stCount('products.kpi_products', products.length) },
        { label: st('products.kpi_bag'), value: count(bagAdds), detail: st('products.of_views', { rate: rate(bagAdds, views) }) },
        { label: st('products.kpi_units'), value: count(units), detail: st('products.kpi_units_detail') },
        { label: st('products.kpi_revenue'), value: <MoneyText list={revenue} />, detail: st('products.kpi_revenue_detail') },
      ]} />
      {selected && <ProductDetail key={selected.productCode} product={selected} onClose={() => pick(null)} />}
      <Panel title={st('products.list_title')} detail={st('products.list_detail')}>
        <StoreTable columns={columns} rows={products} rowKey={(row) => row.productCode} active={picked}
          onRow={(row) => { pick(row.productCode); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
          initialSort={{ key: 'views', direction: 'desc' }} empty={<Empty title={st('products.none')} />} />
      </Panel>
      <Panel title={st('products.lists_title')} detail={st('products.lists_detail')}>
        {report.data.lists.length === 0 ? <Empty title={st('products.no_lists')} /> : (
          <ol className="xo-sf-tiles">
            {report.data.lists.map((list) => (
              <li key={list.list}>
                <span className="xo-sf-tiles-head"><Package aria-hidden="true" className="xo-sf-list-icon" /><strong>{list.list}</strong></span>
                <b>{NUMBER.format(list.impressions)}</b>
                <small>{st('products.shown_in', { products: stCount('products.tile_products', list.products) })}</small>
                <Share value={list.impressions} total={mostLists} />
              </li>
            ))}
          </ol>
        )}
      </Panel>
    </div>
  );
}

export default function StorefrontProducts() {
  const store = useStorefrontSite();
  return <StorefrontGate store={store}>{(site) => <ProductsView site={site} store={store} />}</StorefrontGate>;
}
